import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { RouterModule } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RatingModule } from 'primeng/rating';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-call-to-action',
  standalone: true,
  imports: [CommonModule, CardModule, ButtonModule, RouterModule, RatingModule, FormsModule],
  templateUrl: './call-to-action.component.html',
  styleUrl: './call-to-action.component.scss'
})
export class CallToActionComponent {
  @Input() title = 'Ready to launch your nonprofit’s auction?';
  @Input() body = 'Over 20,000 schools and charities have raised $600M (and counting!) through the BiddingForGood community’s online auctions.';

  // Button
  @Input() ctaLabel = 'Learn how';
  @Input() href?: string;                  // absolute URL
  @Input() routerLink?: any[] | string;    // Angular route
  @Input() target: '_self' | '_blank' = '_self';
  @Input() rel = 'noopener noreferrer';

  // Images (put these in /public or your CDN)
  @Input() imageSrc = '/images/cta-hero.jpg';
  @Input() imageAlt = 'Auction scene';

  // Optional circular gavel badge
  @Input() showBadge = true;
  @Input() badgeImageSrc = '/gavel-badge.png';
  @Input() badgeAlt = 'Gavel';

  @Input() set bodyHtml(value: string | undefined) {
    this._bodyHtml = value ? this.sanitizer.bypassSecurityTrustHtml(value) : undefined;
  }
  _bodyHtml?: SafeHtml;

  @Input() showRating = false;
  @Input() rating = 4.7;         // e.g. 4.7 out of 5
  @Input() reviewCount = 115;    // e.g. 115 reviews
  @Input() outerBgColor: string | null = null // outer p-card background
  @Input() bgColor: string | null = null;      // e.g. '#e9f7ef' or 'transparent'
  @Input() borderColor: string | null = null;  // outer p-card border
  @Input() hasBorder = false;
  @Input() flat = false;                       // remove bg/border/shadow
  @Input() isRaiseMore = false;

  get stars(): ('full' | 'half' | 'empty')[] {
    const v = Math.max(0, Math.min(5, this.rating));
    const full = Math.floor(v);
    const half = v - full >= 0.25 && v - full < 0.75 ? 1 : 0; // round halves
    const adjFull = half ? full : Math.round(v);
    const res = Array(adjFull).fill('full')
      .concat(half ? ['half'] : [])
      .concat(Array(5 - adjFull - (half ? 1 : 0)).fill('empty'));
    return res as any;
  }

  get cardStyle() {
    // Drive the background through a CSS var the SCSS already uses.
    const style: Record<string, string> = {};
    if (this.flat) {
      style['--p-card-background'] = 'transparent';
    } else if (this.bgColor) {
      style['--p-card-background'] = this.bgColor;
    }
    if (this.borderColor && !this.flat) {
      style['border-color'] = this.borderColor;
    }
    return style;
  }

  @Output() ctaClick = new EventEmitter<void>();

  constructor(private sanitizer: DomSanitizer) { }

  onCtaClick() {
    if (!this.href && !this.routerLink) this.ctaClick.emit();
  }
}

